import h from 'hyperscript';
import Headroom from 'headroom.js';
import randomId from '../../../javascripts/utils/random-id';
import icon from '../../../javascripts/utils/icon';

// Hides all expanded containers
function hideExpanded() {
  document.querySelectorAll(
    '.header__toggle[aria-expanded=true]',
  ).forEach($dom => $dom.click());
}

// Collapse trigger
function collapseTrigger(event) {
  // Prevent executing of default link handler
  event.preventDefault();

  // Get DOM elements
  const $trigger = event.srcElement || event.originalTarget;
  const $subnavigation = $trigger.nextElementSibling;

  if ($subnavigation) {
    // Is navigation currently visible?
    const isVisible = $trigger.getAttribute('aria-expanded');

    // Focus active trigger element
    if (isVisible === 'false') $trigger.focus();
    else $trigger.blur();

    // Hide other navigation elements on same level
    if ($subnavigation.getAttribute('data-hide-others') === 'yes' && isVisible === 'false') {
      document
        .querySelectorAll(`.${$subnavigation.getAttribute('data-toggle-class')}`)
        .forEach(
          ($dom) => {
            const $parentItem = document.getElementById($dom.getAttribute('aria-labelledby'));
            $parentItem.setAttribute('aria-expanded', 'false');

            $dom.classList.remove('main-navigation__menu-wrapper--visible');
            $dom.setAttribute('aria-hidden', 'true');
          },
        );
    }

    // Hide search panel
    document
      .querySelectorAll('.header__toggle--item[aria-expanded=true]')
      .forEach($dom => $dom.click());

    // Set attributes and classes
    $trigger.setAttribute('aria-expanded', (isVisible === 'false') ? 'true' : 'false');
    $subnavigation.setAttribute('aria-hidden', (isVisible === 'false') ? 'false' : 'true');
    $subnavigation.classList.toggle($subnavigation.getAttribute('data-toggle-class'));
  }
}

// Add collapse
function addCollapse($subnavigation, withIcon = true, hideOthers = false, overlaySwitch = false) {
  // Get trigger for this subnavigation
  const $link = $subnavigation.previousElementSibling;
  const $trigger = $subnavigation.parentNode;

  // Add information to the subnavigation
  $subnavigation.setAttribute('id', `navigation-${randomId()}`);
  $subnavigation.setAttribute('role', 'group');
  $subnavigation.setAttribute('aria-hidden', 'true');

  // Add information to the trigger
  $link.setAttribute('id', `navigation-${randomId()}`);
  $link.setAttribute('aria-expanded', 'false');
  $link.setAttribute('aria-haspopup', 'true');
  $link.setAttribute('aria-controls', $subnavigation.id);

  // Collapse icon
  if (withIcon) {
    $link.appendChild(icon({
      icon: 'navigation-collapse',
      viewBox: '0 1 10 6',
      classes: ['main-navigation__collapse-icon'],
      paths: ['M4.96 4.54L1.7 1.3C1.33.9.7.9.3 1.3c-.4.38-.4 1.02 0 1.4l3.95 3.96c.4.4 1.02.4 1.4 0L9.63 2.7c.4-.38.4-1.02 0-1.4-.4-.4-1.02-.4-1.4 0L4.95 4.53z'],
    }));
  }

  // Add missing ID from trigger to subnavigation
  $subnavigation.setAttribute('aria-labelledby', $link.id);

  // Class name
  $subnavigation.setAttribute('data-toggle-class', `${$subnavigation.className}--visible`);
  $subnavigation.setAttribute('data-hide-others', hideOthers ? 'yes' : 'no');
  $subnavigation.setAttribute('data-overlay-switch', overlaySwitch ? 'yes' : 'no');

  // Add delayed hover events to trigger
  $trigger.addEventListener('mouseover', collapseTrigger);
  $trigger.addEventListener('mouseout', collapseTrigger);
}

function removeCollapse($subnavigation) {
  // Get trigger for this subnavigation
  const $trigger = $subnavigation.previousElementSibling;

  // Remove active class
  $subnavigation.classList.remove($subnavigation.getAttribute('data-toggle-class'));

  // Remove attributes
  $subnavigation.removeAttribute('role');
  $subnavigation.removeAttribute('aria-hidden');
  $subnavigation.removeAttribute('aria-labelledby');
  $subnavigation.removeAttribute('data-toggle-class');
  $trigger.removeAttribute('aria-expanded');
  $trigger.removeAttribute('aria-haspopup');
  $trigger.removeAttribute('aria-controls');

  // Remove icon
  $trigger
    .querySelectorAll('.main-navigation__collapse-icon')
    .forEach($dom => $dom.remove());

  // Remove click handler
  $trigger.removeEventListener('click', collapseTrigger);
}

// Runs on changes of viewport
function navigationListener(mediaQuery) {
  // Get items
  const $header = document.querySelector('.header');
  const $navigation = $header.querySelector('.header__navigation');
  const $mainNavigation = $header.querySelector('.main-navigation');
  const $sidebar = $header.querySelector('.header__sidebar');
  const $panel = $navigation.querySelector('.header__navigation-panel');
  const $search = $header.querySelector('.header__search');

  // Init mobile navigation
  if (mediaQuery.matches) {
    // 1. Remove desktop navigation collapse handling and close buttons
    $panel
      .querySelectorAll('.main-navigation__menu-wrapper')
      .forEach(removeCollapse);

    // 2. Delete close buttons
    $navigation
      .querySelectorAll('.main-navigation__close-button')
      .forEach($dom => $dom.remove());

    // 3. Create button for navigation toggle
    let isMenuOpen = false;
    const $button = h('button.header__toggle.header__toggle--navigation', {
      attrs: {
        'aria-expanded': 'false',
        'aria-controls': $panel.id,
        'aria-label': 'Navigation anzeigen oder ausblenden',
        role: 'button',
      },
      onclick(event) {
        event.stopPropagation();
        event.preventDefault();

        // Hide search panel
        document
          .querySelectorAll('.header__toggle--item[aria-expanded=true]')
          .forEach($dom => $dom.click());

        // Open/close menu
        isMenuOpen = !isMenuOpen;

        this.setAttribute('aria-expanded', isMenuOpen ? 'true' : 'false');
        $sidebar.classList.toggle('header__sidebar--visible');
        document.querySelector('.header__sidebar-page[data-id="0"]').classList.toggle('header__sidebar-page--active');
        document.documentElement.classList.toggle('disable-scroll');
      },
    }, h('span.header__hamburger-icon',
      h('span.header__hamburger-icon-bar'),
      h('span.header__hamburger-icon-bar'),
      h('span.header__hamburger-icon-bar'),
    ));

    // 4. Insert button into DOM
    $navigation.insertBefore($button, $panel);

    // 5. Enable collapsing for navigation items
    $panel
      .querySelectorAll('.main-navigation__section-list, .main-navigation__menu-wrapper')
      .forEach($subnavigation => addCollapse($subnavigation, true));

    // 6. Close search
    document
      .querySelectorAll('.header__toggle--item[aria-expanded=true]')
      .forEach($dom => $dom.click());

    // 7. Move search dom back
    if ($panel.contains($search) === true) {
      const $wrapper = $panel.querySelector('.header__navigation-wrap');
      const $panelMainNavigation = $panel.querySelector('.main-navigation');

      $panel.insertBefore($panelMainNavigation, $wrapper);
      $wrapper.remove();
      $header.appendChild($search);
    }

    document.querySelector('.header__toggle--item').addEventListener('focus', () => {
      if (isMenuOpen) {
        $button.click();
      }
    });

  // Init desktop navigation
  } else {
    // 1. Remove mobile navigation collapse handling
    $panel
      .querySelectorAll('.main-navigation__section-list, .main-navigation__menu-wrapper')
      .forEach(removeCollapse);

    // 2. Delete navigation toggle if it exists
    $navigation
      .querySelectorAll('.header__toggle--navigation')
      .forEach($dom => $dom.remove());

    // 3. Reset body, panel and menu overlay classes
    $sidebar.classList.remove('header__sidebar--visible');
    document.documentElement.classList.remove('disable-scroll');

    // Hide on main navigation
    $mainNavigation.addEventListener('click', () => {
      hideExpanded();
    });

    // 5. Enable collapsing for navigation items and buttons
    $panel
      .querySelectorAll('.main-navigation__menu-wrapper')
      .forEach(($menu) => {
        addCollapse($menu, false, true, true);
      });

    // 6. Close search
    document
      .querySelectorAll('.header__toggle--item[aria-expanded=true]')
      .forEach($dom => $dom.click());

    // 7. Move search into navigation and wrap it
    if ($panel.contains($search) === false) {
      const $wrapper = h('div.header__navigation-wrap');
      const $panelMainNavigation = $panel.querySelector('.main-navigation');

      $panel.insertBefore($wrapper, $panelMainNavigation);
      $wrapper.appendChild($panelMainNavigation);
      $wrapper.appendChild($search);
    }
  }
}

function searchOverlay($panel) {
  let focusableEls = $panel.querySelectorAll('a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]');
  focusableEls = Array.prototype.slice.call(focusableEls);

  focusableEls.unshift(document.querySelector('.header__toggle'));

  let firstFocusableEl = focusableEls[0];
  let lastFocusableEl = focusableEls[ focusableEls.length - 1 ];

  $panel.addEventListener('keydown', function(e) {
      var KEY_TAB = 9;

      function handleBackwardTab() {
          if ( document.activeElement === firstFocusableEl ) {
              e.preventDefault();
              lastFocusableEl.focus();
          }
      }
      function handleForwardTab() {
          if ( document.activeElement === lastFocusableEl ) {
              e.preventDefault();
              firstFocusableEl.focus();
          }
      }

      switch(e.keyCode) {
        case KEY_TAB:
          if ( focusableEls.length === 1 ) {
            e.preventDefault();
            break;
          }

          if ( e.shiftKey ) {
            handleBackwardTab();
          } else {
            handleForwardTab();
          }
          break;
        default:
          break;
      }
  });
}

function hideAllMenuItems($nav) {
  const $menuItems = $nav.querySelectorAll('.main-navigation__link--focus');

  for (let i = 0; i < $menuItems.length; i += 1) {
    $menuItems[i].classList.remove('main-navigation__link--focus');
  }
}

function navigationTabbing($nav) {
  const $items = $nav.querySelectorAll('.main-navigation__link');

  Array.from($items).forEach((item) => {
    item.addEventListener('focus', () => {
      hideAllMenuItems($nav);

      item.classList.toggle('main-navigation__link--focus');
    });

    item.addEventListener('mouseout', () => {
      hideAllMenuItems($nav);
    });
  });

  const $search = document.querySelector('.header__toggle');
  const $logo = document.querySelector('.header__logo .logo');

  $search.addEventListener('focus', () => {
    hideAllMenuItems($nav);
  });
  $logo.addEventListener('focus', () => {
    hideAllMenuItems($nav);
  });
}

// Init navigation
document.querySelectorAll('.header').forEach(($header) => {
  // Get items
  const $navigation = $header.querySelector('.header__navigation');
  const $mainNavigation = $header.querySelector('.main-navigation');
  const $panel = $navigation.querySelector('.header__navigation-panel');
  const $search = $header.querySelector('.header__search');
  const $searchPanel = $search.querySelector('.header__search-panel');
  // create print logo
  const $logo = $header.querySelector('.header__logo');
  const $logoForPrint = $logo.cloneNode(true);
  $logoForPrint.setAttribute('class', 'only-print');
  if (document.querySelector('.header__wrapper')) {
    document.querySelector('.header__wrapper').insertBefore($logoForPrint, document.querySelector('.header'));
  }

  // offset for header to show
  const offset = window.matchMedia('(max-width: 999px)').matches ? 50 : 130;

  // Setup Headroom
  const headroom = new Headroom($header, {
    offset,
    tolerance: 2,
    classes: {
      initial: 'header--initialized',
      pinned: 'header--pinned',
      unpinned: 'header--unpinned',
      top: 'header--top',
      notTop: 'header--not-top',
      bottom: 'header--bottom',
      notBottom: 'header--not-bottom',
    },
    onPin() {
      if ($header.classList.contains('headroom--frozen')) {
        this.unpin();
      }
    },
  });

  // Initialize Headroom
  headroom.init();

  // Create search button
  let isSearchOpen = false;
  const $searchButton = h('button.header__toggle.header__toggle--item', {
    attrs: {
      'aria-expanded': 'false',
      'aria-controls': $panel.id,
      'aria-label': 'Suche anzeigen oder ausblenden',
    },
    onclick(event) {
      event.stopPropagation();
      event.preventDefault();

      // Hide navigation panel or menus
      document.querySelectorAll(
        '.header__toggle--navigation[aria-expanded=true]',
      ).forEach($dom => $dom.click());

      // Open/close menu
      isSearchOpen = !isSearchOpen;
      this.setAttribute('aria-expanded', isSearchOpen ? 'true' : 'false');
      $searchPanel.classList.toggle('header__search-panel--visible');
      $mainNavigation.classList.toggle('main-navigation--disabled');
      document.documentElement.classList.toggle('disable-scroll');

      // Focus on input
      if (isSearchOpen) {
        $searchPanel.querySelector('.input').focus();
      }
    },
  }, icon({
    icon: 'magnifier',
    focusable: false,
    title: 'Suche anzeigen oder ausblenden',
    classes: ['header__search-icon'],
  }),
    h('span.header__hamburger-icon',
      h('span.header__hamburger-icon-bar'),
      h('span.header__hamburger-icon-bar'),
      h('span.header__hamburger-icon-bar'),
    ),

    h('span.header__item-title', 'Suche'),
    h('span.header__item-title--close', 'schließen'),
  );

  // Add search button
  $search.insertBefore($searchButton, $searchPanel);

  // Create media query for navigation switch
  const navigationMediaQuery = window.matchMedia('(max-width: 999px)');

  // Add listener for changes in viewport
  navigationMediaQuery.addListener(navigationListener);

  // First run
  navigationListener(navigationMediaQuery);

  // tab routine for open overlay
  searchOverlay($searchPanel);

  // navigation tabbing
  navigationTabbing($mainNavigation);
});

// Sidebar navigation
class SidebarNavigation {
  constructor($el) {
    this.$el = $el;
    this.$navigationContainer = this.$el.querySelector('.header__sidebar-container--navigation');

    this.dataSourceUrl = this.$el.dataset.source;

    this.loadNavigationData();
  }

  loadNavigationData() {
    this.get(this.dataSourceUrl, this.init.bind(this));
  }

  init(data) {
    this.data = data;

    this.buildPages(this.data);
    // Calculate mobile menu height
    const menuListHeights = [];
    document.querySelectorAll('.header__sidebar-navigation').forEach((item => menuListHeights.push((item.offsetHeight) ? item.offsetHeight : 0)));
    const maxListHeight = Math.max(...menuListHeights);
    document.querySelector('.header__sidebar-container--navigation').style.minHeight = `${maxListHeight}px`;

    this.initEvents();
  }

  buildPages(
    items,
    pageId = 0,
    zIndex = 0,
    settings = { closable: false, firstPage: true },
  ) {
    // Build pages recursively
    const $items = items.map((item) => {
      if (item.children && item.children.length) {
        this.buildPages(
          item.children,
          item.children.map(i => i.uid).join('-'),
          zIndex + 1,
          { closable: true, firstPage: false },
        );
      }
      const itemAttrs = item.children && item.children.length ? { attrs: { 'data-page-id': item.children.map(i => i.uid).join('-'), 'data-level': 0 } } : {};
      const activeClass = item.current ? '.header__sidebar-navigation-item--active' : '';
      const itemArrow = item.children && item.children.length ? h('button.header__sidebar-navigation-arrow', { attrs: { 'aria-expanded': false, 'aria-label': 'Unternavigation anzeigen', 'aria-controls': 'header__sidebar-page-' + item.children.map(i => i.uid).join('-') } }) : null;

      return h(`li.header__sidebar-navigation-item${activeClass}`, itemAttrs,
        h('a.header__sidebar-navigation-link', { href: item.uri, innerHTML: item.title }),
        itemArrow,
      );
    });

    // Add back entry, if page is closable
    if (settings.closable) {
      $items.unshift(
        h('li.header__sidebar-navigation-item.header__sidebar-navigation-item--close',
          h('a.header__sidebar-navigation-link.header__sidebar-navigation-link--action',
            { href: '#', innerHTML: '&laquo; zurück' },
          ),
        ),
      );
    }

    // First page is visible
    const pageClass = '.header__sidebar-page';

    // Append to navigation container
    this.$navigationContainer.append(
      h(pageClass, { attrs: { id: 'header__sidebar-page-' + pageId,  'data-id': pageId, style: `z-index: ${zIndex};` } },
        h('ul.header__sidebar-navigation',
          { attrs: { role: 'menu', 'aria-label': 'Seiten-Navigation' } }, $items,
        ),
      ),
    );
  }

  initEvents() {
    // Page Links
    const $pageLinks = this.$navigationContainer.querySelectorAll(
      'li.header__sidebar-navigation-item[data-page-id] .header__sidebar-navigation-arrow',
    );

    $pageLinks.forEach(($link) => {
      $link.addEventListener('click', (event) => {
        event.stopPropagation();

        const $target = event.currentTarget.closest('.header__sidebar-navigation-item');
        const pageId = $target.dataset.pageId;

        const $menuContainer = this.$navigationContainer.querySelector(`.header__sidebar-page[data-id='${pageId}']`);

        $menuContainer.classList.toggle('header__sidebar-page--active');
        $link.setAttribute('aria-expanded', 'true');

        // focus isn't working without timeout
        setTimeout(() => {
          $menuContainer.querySelector('.header__sidebar-navigation-link--action').focus();
          document.querySelector('.header__sidebar-page[data-id="0"]').classList.remove('header__sidebar-page--active');
        }, 300);



      });
    });

    // Close links
    const $closeLinks = this.$navigationContainer.querySelectorAll(
      'li.header__sidebar-navigation-item--close',
    );

    $closeLinks.forEach(($link) => {
      $link.addEventListener('click', (event) => {
        event.stopPropagation();
        event.preventDefault();

        const $target = event.currentTarget;
        const $menuContainer = $target.closest('.header__sidebar-page--active');

        if ($menuContainer) {
          $menuContainer.classList.toggle('header__sidebar-page--active');

          const $rootNavigation = document.querySelector('.header__sidebar-page[data-id="0"]');
          $rootNavigation.classList.add('header__sidebar-page--active');
          $rootNavigation.querySelector('.header__sidebar-navigation-arrow[aria-expanded="true"]').setAttribute('aria-expanded', 'false');
        }
      });
    });
  }

  get(url, successCallback) {
    this.req = new XMLHttpRequest();
    this.req.open('get', url);
    this.req.onload = () => {
      if (this.req.status === 200) successCallback(JSON.parse(this.req.response));
      else Error(this.req.statusText);
    };

    this.req.send();
  }
}

// Init sidebar navigation
document.querySelectorAll('.header__sidebar').forEach(
  $el => new SidebarNavigation($el),
);



// WEBPACK FOOTER //
// ./src/components/organisms/header/header.js